import { makeAutoObservable, runInAction } from 'mobx'

import { diskErrorText } from '../Helpers/diskErrors'
import { LoadDiskNodeHistoryTask } from '../Tasks/LoadDiskNodeHistoryTask'
import { LoadDiskNodeTask } from '../Tasks/LoadDiskNodeTask'

import type { DiskHistoryItem, DiskNode } from '../Contracts/DiskNode'

/**
 * Властивості вузла: картка (властивості, шлях) і журнал.
 *
 * Бічна панель «Інформація» переглядача (`FileViewerStore.info`) і дровер
 * «Властивості» екрана диска (`DiskPropertiesStore.info`).
 * `open` завершується, щойно прийшла картка; журнал довантажується окремо.
 * Рядок списку (`preview`) показується одразу, поки йде картка: перемикання
 * рядків при відкритому дровері не блимає скелетоном.
 */
export class DiskNodeInfoStore {
    uuid: string | null = null
    node: DiskNode | null = null
    history: DiskHistoryItem[] = []
    historyTotal = 0
    isLoading = false
    isHistoryLoading = false
    error: string | null = null

    private requestId = 0

    constructor() {
        makeAutoObservable<DiskNodeInfoStore, 'requestId'>(this, { requestId: false })
    }

    get isOpen(): boolean {
        return this.uuid !== null
    }

    /**
     * @param preview рядок списку того самого вузла — видно одразу, картка його замінить
     * @param force перечитати, навіть якщо цей вузол уже відкрито (після перейменування, нової версії)
     */
    async open(uuid: string, preview: DiskNode | null = null, force = false): Promise<void> {
        if (!force && this.uuid === uuid && (this.node !== null || this.isLoading)) return

        const id = ++this.requestId

        this.uuid = uuid
        this.node = preview !== null && preview.uuid === uuid ? preview : null
        this.history = []
        this.historyTotal = 0
        this.error = null
        this.isLoading = true
        this.isHistoryLoading = true

        // Журнал — паралельно й без очікування: переглядач показує вміст, щойно є картка
        void this.loadHistory(uuid, id)

        try {
            const node = await LoadDiskNodeTask(uuid)

            runInAction(() => {
                if (id === this.requestId) this.node = node
            })
        } catch (error) {
            runInAction(() => {
                if (id === this.requestId) this.error = diskErrorText(error, 'Не вдалося відкрити файл')
            })
        } finally {
            runInAction(() => {
                if (id === this.requestId) this.isLoading = false
            })
        }
    }

    private async loadHistory(uuid: string, id: number): Promise<void> {
        try {
            const history = await LoadDiskNodeHistoryTask(uuid)

            runInAction(() => {
                if (id !== this.requestId) return
                this.history = history.items
                this.historyTotal = history.total
            })
        } catch {
            // Журнал — додаткове: без нього властивості й кнопки лишаються
        } finally {
            runInAction(() => {
                if (id === this.requestId) this.isHistoryLoading = false
            })
        }
    }

    close(): void {
        this.requestId += 1
        this.uuid = null
        this.node = null
        this.history = []
        this.historyTotal = 0
        this.error = null
        this.isLoading = false
        this.isHistoryLoading = false
    }
}
